/** @format */

import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, input, output } from '@angular/core';
import { HomefieldIconLibraryModule, HomefieldIconsLibraryService, homefieldIconCaretDown } from '@dsg-tech/homefield-angular-icons';
import { Observable, Subject } from 'rxjs';
import {
	analyticsSortMethodValues,
	familyPageDropdownValues,
	searchDropdownValues
} from '@plp/data-access/constants';
import { map, takeUntil } from 'rxjs/operators';
import {
	refreshProductList,
	selectProductListSEO,
	selectSelectedSort,
	updateSelectedSort
} from '@plp/data-access/state';

import { ProductAnalyticsService } from '@shared/data-access/services';
import { Store } from '@ngrx/store';
import { UtilityService } from '@shared/utils/services';

@Component({
	selector: 'plp-components-sort',
	imports: [AsyncPipe, NgClass, HomefieldIconLibraryModule],
	templateUrl: './sort.component.html',
	styleUrls: ['./sort.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SortComponent implements OnDestroy {
	@Input() isDisabled = false;
	sortPositionChangeEnabled = input(false);
	tabPressed = output<void>();
	private readonly destroy$ = new Subject<void>();

	readonly dropdownValues$: Observable<{ label: string; value: number }[]> = this.store
		.select(selectProductListSEO)
		.pipe(
			takeUntil(this.destroy$),
			map((SEOData) =>
				SEOData && SEOData.catgroupId ? familyPageDropdownValues : searchDropdownValues
			)
		);
	readonly selectedValue$: Observable<{ label: string; value: number }> = this.store
		.select(selectSelectedSort)
		.pipe(takeUntil(this.destroy$));

	constructor(
		private readonly analyticsService: ProductAnalyticsService,
		private readonly store: Store,
		private readonly utilityService: UtilityService,
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService
	) {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconCaretDown]);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	selectValue(selector: HTMLSelectElement): void {
		// Since a <select> element can have multiple selections if it's configured
		// for multiple selection (<select multiple>),
		// we use [0] to target the first (and only) selected option.
		const selectedOption = selector.selectedOptions[0];

		const selectedLabel = selectedOption.label;
		const selectedValue = selectedOption.value;

		this.store.dispatch(
			updateSelectedSort({
				selectedSort: { label: selectedLabel, value: parseInt(selectedValue) }
			})
		);
		this.store.dispatch(refreshProductList());
		const analyticsValue = analyticsSortMethodValues.find(
			(dropdownOption) => parseInt(selectedValue) === dropdownOption.value
		)?.text;
		this.setAnalyticsEvent(analyticsValue);
		selector.blur();
		const plpContainerElem = document.getElementById('plp-list-container');
		this.utilityService.scrollToElement(plpContainerElem, { behavior: 'instant' });
	}

	private setAnalyticsEvent(sortValue: string): void {
		const refinementUpdateObj = { UpdateType: 'SortMethod', UpdateValue: sortValue };
		this.analyticsService.triggerAnalyticsEvent('ResultsViewUpdate', refinementUpdateObj);
	}

	onTabKeyDown() {
		this.tabPressed.emit();
	}
}
